import {
  deleteQueryKeys,
  readOptionalBooleanParam,
  readOptionalNumberParam,
  readRepeatedTrimmedParams,
  readTrimmedParam,
  writeOptionalBooleanParam,
  writeOptionalNumberParam,
  writeRepeatedParams,
  writeTrimmedParam,
} from "@/lib/filterUrlParams";
import type { ContactFilter } from "@/types/entities";

/** Keys owned by list-filter URL sync (not deep links like `contactId`). */
export const CONTACT_FILTER_PARAM_KEYS = [
  "search",
  "archived",
  "profileIntention",
  "rentalIntentionFurnishing",
  "minBedroom",
  "priceFrom",
  "priceTo",
  "isActiveProfile",
  "crmSyncStatus",
  "labelIds",
  "labelName",
] as const;

export const CLEARED_CONTACT_FILTER: Partial<ContactFilter> = {
  search: undefined,
  archived: undefined,
  profileIntention: undefined,
  rentalIntentionFurnishing: undefined,
  minBedroom: undefined,
  priceFrom: undefined,
  priceTo: undefined,
  isActiveProfile: undefined,
  crmSyncStatus: undefined,
  labelIds: undefined,
};

export function labelIdsFromSearchParams(params: URLSearchParams): string[] {
  return readRepeatedTrimmedParams(params, "labelIds");
}

export function parseContactFilterFromSearchParams(params: URLSearchParams): ContactFilter {
  const filter: ContactFilter = {};
  const search = readTrimmedParam(params, "search");
  if (search) filter.search = search;
  const archived = readOptionalBooleanParam(params, "archived");
  if (archived !== undefined) filter.archived = archived;
  const profileIntention = readTrimmedParam(params, "profileIntention");
  if (profileIntention) filter.profileIntention = profileIntention;
  const rentalIntentionFurnishing = readTrimmedParam(params, "rentalIntentionFurnishing");
  if (rentalIntentionFurnishing) filter.rentalIntentionFurnishing = rentalIntentionFurnishing;
  const minBedroom = readOptionalNumberParam(params, "minBedroom");
  if (minBedroom !== undefined) filter.minBedroom = minBedroom;
  const priceFrom = readOptionalNumberParam(params, "priceFrom");
  if (priceFrom !== undefined) filter.priceFrom = priceFrom;
  const priceTo = readOptionalNumberParam(params, "priceTo");
  if (priceTo !== undefined) filter.priceTo = priceTo;
  const isActiveProfile = readOptionalBooleanParam(params, "isActiveProfile");
  if (isActiveProfile !== undefined) filter.isActiveProfile = isActiveProfile;
  const crmSyncStatus = readTrimmedParam(params, "crmSyncStatus");
  if (crmSyncStatus) filter.crmSyncStatus = crmSyncStatus;
  const labelIds = labelIdsFromSearchParams(params);
  if (labelIds.length > 0) filter.labelIds = labelIds;
  return filter;
}

export function applyContactFilterToSearchParams(
  filter: ContactFilter,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, CONTACT_FILTER_PARAM_KEYS);
  writeTrimmedParam(next, "search", filter.search);
  writeOptionalBooleanParam(next, "archived", filter.archived);
  writeTrimmedParam(next, "profileIntention", filter.profileIntention);
  writeTrimmedParam(next, "rentalIntentionFurnishing", filter.rentalIntentionFurnishing);
  writeOptionalNumberParam(next, "minBedroom", filter.minBedroom);
  writeOptionalNumberParam(next, "priceFrom", filter.priceFrom);
  writeOptionalNumberParam(next, "priceTo", filter.priceTo);
  writeOptionalBooleanParam(next, "isActiveProfile", filter.isActiveProfile);
  writeTrimmedParam(next, "crmSyncStatus", filter.crmSyncStatus);
  writeRepeatedParams(next, "labelIds", filter.labelIds);
  return next;
}

function canonicalQuery(filter: ContactFilter): string {
  return applyContactFilterToSearchParams(filter, new URLSearchParams()).toString();
}

export function contactFiltersEquivalent(a: ContactFilter, b: ContactFilter): boolean {
  return canonicalQuery(a) === canonicalQuery(b);
}

export const contactFilterUrlSyncConfig = {
  parseFromSearchParams: parseContactFilterFromSearchParams,
  applyToSearchParams: applyContactFilterToSearchParams,
  filtersEquivalent: contactFiltersEquivalent,
  clearedFilter: CLEARED_CONTACT_FILTER,
};
